import { useMemo } from 'react';

export type AccountCategorySlug = 'asset' | 'liability' | 'equity' | 'revenue' | 'expense';

export interface AccountEditRow {
  id: string;
  number?: string;
  name: string;
  accountType: string;
  detailType: string;
  description?: string;
  novalaBalance?: number;
  bankBalance?: number | null;
  locked: boolean;
  parentId?: string;
  categorySlug: AccountCategorySlug;
}

export interface AccountCategory {
  slug: AccountCategorySlug;
  label: string;
  accounts: AccountEditRow[];
}

export const SECTION_ORDER: AccountCategorySlug[] = ['asset', 'liability', 'equity', 'revenue', 'expense'];

export const SECTION_LABELS: Record<AccountCategorySlug, string> = {
  asset: 'Asset',
  liability: 'Liability',
  equity: 'Equity',
  revenue: 'Revenue',
  expense: 'Expense',
};

const STORAGE_KEY = 'novala.editModeAccounts';

const MOCK_ACCOUNTS: AccountEditRow[] = [
  { id: 'a1', number: '1010', name: 'Business Chequing', accountType: 'Bank', detailType: 'Chequing', novalaBalance: 12480.52, bankBalance: 12480.52, locked: false, categorySlug: 'asset' },
  { id: 'a2', number: '1020', name: 'Business Savings', accountType: 'Bank', detailType: 'Savings', novalaBalance: 4210.00, bankBalance: 4210.00, locked: false, categorySlug: 'asset' },
  { id: 'a3', number: '1030', name: 'Petty Cash', accountType: 'Bank', detailType: 'Cash on hand', novalaBalance: 320.00, bankBalance: null, locked: false, categorySlug: 'asset' },
  { id: 'a4', number: '1200', name: 'Accounts Receivable (A/R)', accountType: 'Accounts receivable (A/R)', detailType: 'Accounts Receivable (A/R)', novalaBalance: 936.00, bankBalance: null, locked: true, categorySlug: 'asset' },
  { id: 'a5', number: '1300', name: 'Inventory Asset', accountType: 'Other current assets', detailType: 'Inventory', novalaBalance: 1800.00, bankBalance: null, locked: false, categorySlug: 'asset' },
  { id: 'a6', number: '1310', name: 'Prepaid Rent', accountType: 'Other current assets', detailType: 'Prepaid Expenses', novalaBalance: 1200.00, bankBalance: null, locked: false, categorySlug: 'asset' },
  { id: 'a7', number: '1320', name: 'Prepaid Insurance', accountType: 'Other current assets', detailType: 'Prepaid Expenses', novalaBalance: 620.00, bankBalance: null, locked: false, categorySlug: 'asset' },
  { id: 'a8', number: '1350', name: 'Undeposited Funds', accountType: 'Other current assets', detailType: 'Undeposited Funds', novalaBalance: 0, bankBalance: null, locked: false, categorySlug: 'asset' },
  { id: 'a9', number: '1500', name: 'Office Equipment', accountType: 'Fixed assets', detailType: 'Furniture and equipment', novalaBalance: 3420.00, bankBalance: null, locked: false, categorySlug: 'asset' },
  { id: 'a10', number: '1510', name: 'Vehicles', accountType: 'Fixed assets', detailType: 'Vehicles', novalaBalance: 11200.00, bankBalance: null, locked: false, categorySlug: 'asset' },
  { id: 'a11', number: '1590', name: 'Accumulated Depreciation', accountType: 'Fixed assets', detailType: 'Accumulated depreciation', novalaBalance: -2500.00, bankBalance: null, locked: false, categorySlug: 'asset' },
  { id: 'a12', number: '1700', name: 'Security Deposits', accountType: 'Other assets', detailType: 'Other assets', novalaBalance: 2000.00, bankBalance: null, locked: false, categorySlug: 'asset' },
  { id: 'a13', number: '1400', name: 'Payroll Refunds', accountType: 'Other current assets', detailType: 'Other current assets', novalaBalance: 150.00, bankBalance: null, locked: false, categorySlug: 'asset' },
  { id: 'l1', number: '2010', name: 'Accounts Payable (A/P)', accountType: 'Accounts payable (A/P)', detailType: 'Accounts Payable (A/P)', novalaBalance: -1500.00, bankBalance: null, locked: true, categorySlug: 'liability' },
  { id: 'l2', number: '2200', name: 'Payroll Liabilities', accountType: 'Other current liabilities', detailType: 'Payroll liabilities', novalaBalance: -890.00, bankBalance: null, locked: false, categorySlug: 'liability' },
  { id: 'l3', number: '2210', name: 'Federal Taxes', accountType: 'Other current liabilities', detailType: 'Payroll liabilities', novalaBalance: -450.00, bankBalance: null, locked: false, parentId: 'l2', categorySlug: 'liability' },
  { id: 'l4', number: '2100', name: 'Credit Card', accountType: 'Credit card', detailType: 'Credit card', novalaBalance: -320.00, bankBalance: -320.00, locked: false, categorySlug: 'liability' },
  { id: 'l5', number: '2500', name: 'Loan Payable', accountType: 'Long-term liabilities', detailType: 'Notes payable', novalaBalance: -5000.00, bankBalance: null, locked: false, categorySlug: 'liability' },
  { id: 'e1', number: '3010', name: "Owner's Equity", accountType: 'Equity', detailType: "Owner's equity", novalaBalance: 7036.52, bankBalance: null, locked: false, categorySlug: 'equity' },
  { id: 'e2', number: '3020', name: 'Retained Earnings', accountType: 'Equity', detailType: 'Retained earnings', novalaBalance: 8200.00, bankBalance: null, locked: true, categorySlug: 'equity' },
  { id: 'r1', number: '4010', name: 'Services Income', accountType: 'Income', detailType: 'Service/fee income', novalaBalance: 32400.00, bankBalance: null, locked: false, categorySlug: 'revenue' },
  { id: 'r2', number: '4020', name: 'Product Sales', accountType: 'Income', detailType: 'Sales of product income', novalaBalance: 8900.00, bankBalance: null, locked: false, categorySlug: 'revenue' },
  { id: 'r3', number: '4030', name: 'Other Income', accountType: 'Other income', detailType: 'Other miscellaneous income', novalaBalance: 1500.00, bankBalance: null, locked: false, categorySlug: 'revenue' },
  { id: 'x1', number: '5010', name: 'Office Supplies', accountType: 'Expenses', detailType: 'Office/general administrative expenses', novalaBalance: 420.00, bankBalance: null, locked: false, categorySlug: 'expense' },
  { id: 'x2', number: '5020', name: 'Rent', accountType: 'Expenses', detailType: 'Rent or lease of buildings', novalaBalance: 2400.00, bankBalance: null, locked: false, categorySlug: 'expense' },
  { id: 'x3', number: '5030', name: 'Utilities', accountType: 'Expenses', detailType: 'Utilities', novalaBalance: 380.00, bankBalance: null, locked: false, categorySlug: 'expense' },
  { id: 'x4', number: '5040', name: 'Phone and Internet', accountType: 'Expenses', detailType: 'Utilities', novalaBalance: 220.00, bankBalance: null, locked: false, categorySlug: 'expense' },
  { id: 'x5', number: '5050', name: 'Insurance', accountType: 'Expenses', detailType: 'Insurance', novalaBalance: 180.00, bankBalance: null, locked: false, categorySlug: 'expense' },
  { id: 'x6', number: '5060', name: 'Professional Fees', accountType: 'Expenses', detailType: 'Legal and professional fees', novalaBalance: 1200.00, bankBalance: null, locked: false, categorySlug: 'expense' },
  { id: 'x7', number: '5070', name: 'Travel', accountType: 'Expenses', detailType: 'Travel', novalaBalance: 650.00, bankBalance: null, locked: false, categorySlug: 'expense' },
  { id: 'x8', number: '5080', name: 'Meals and Entertainment', accountType: 'Expenses', detailType: 'Meals and entertainment', novalaBalance: 340.00, bankBalance: null, locked: false, categorySlug: 'expense' },
  { id: 'x9', number: '5090', name: 'Advertising', accountType: 'Expenses', detailType: 'Advertising/promotional', novalaBalance: 480.00, bankBalance: null, locked: false, categorySlug: 'expense' },
  { id: 'x10', number: '5100', name: 'Bank Fees', accountType: 'Expenses', detailType: 'Bank fees', novalaBalance: 75.00, bankBalance: null, locked: false, categorySlug: 'expense' },
  { id: 'x11', number: '5200', name: 'Payroll Expenses', accountType: 'Expenses', detailType: 'Payroll expenses', novalaBalance: 4800.00, bankBalance: null, locked: false, categorySlug: 'expense' },
];

export function useAccountCategories() {
  const data = useMemo<AccountCategory[]>(() => {
    const search = typeof window !== 'undefined' ? window.location.search : '';
    const params = new URLSearchParams(search);
    const emptyFlag = params.get('empty') === '1';

    let accounts: AccountEditRow[] = MOCK_ACCOUNTS;

    if (emptyFlag) {
      accounts = [];
    } else {
      try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (raw) {
          const stored = JSON.parse(raw) as AccountEditRow[];
          if (Array.isArray(stored) && stored.length > 0) {
            accounts = stored;
          }
        }
      } catch {}
    }

    return SECTION_ORDER.map((slug) => ({
      slug,
      label: SECTION_LABELS[slug],
      accounts: accounts.filter((a) => a.categorySlug === slug),
    }));
  }, []);

  return { data, loading: false, error: null as string | null };
}

export function persistAccounts(accounts: AccountEditRow[]) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(accounts));
  } catch {}
}